/**
 * Task 151-D.2 — pure helpers for recording a package service delivery.
 *
 * React-free so they are unit-testable in the Node vitest env (mirrors
 * `progressView.ts` / `entitlement.ts`). The UI (`RecordDeliverySection` in
 * `ClientServicesPage`) builds its wire payload through `buildRecordDeliveryInput`
 * and lists pickable services through `recordableServices`.
 */
import type { AssignmentServiceSnapshot, RecordPackageServiceDeliveryInput } from '../../api/types';

export interface RecordableService {
  readonly service_id: string;
  readonly service_name: string;
}

/**
 * Services eligible for recording a delivery: the assignment snapshot's
 * INCLUDED services (parity with the `record-package-service-delivery` EF,
 * which rejects any service that is not an included snapshot line).
 */
export function recordableServices(
  snapshot: ReadonlyArray<AssignmentServiceSnapshot> | null | undefined,
): ReadonlyArray<RecordableService> {
  return (snapshot ?? [])
    .filter((s) => s.included !== false && typeof s.service_id === 'string')
    .map((s) => ({ service_id: s.service_id, service_name: s.service_name || s.service_id }));
}

export interface RecordDeliveryFormState {
  readonly serviceId: string;
  /** Raw text from the number input. */
  readonly quantity: string;
  /** 'YYYY-MM-DD' from a date input; empty → EF defaults to now. */
  readonly deliveredAt: string;
  readonly notes: string;
}

export type BuildRecordDeliveryResult =
  | { readonly ok: true; readonly input: RecordPackageServiceDeliveryInput }
  | { readonly ok: false; readonly error: string };

/**
 * Validate the form and build the wire input. Quantity must be a positive
 * finite number (mirrors the EF). `delivered_at` / `notes` are included only
 * when set, so the EF applies its defaults (now / null).
 */
export function buildRecordDeliveryInput(
  assignmentId: string,
  form: RecordDeliveryFormState,
): BuildRecordDeliveryResult {
  if (!form.serviceId) return { ok: false, error: 'اختر الخدمة.' };

  const qty = Number(form.quantity);
  if (!Number.isFinite(qty) || qty <= 0) {
    return { ok: false, error: 'الكمية يجب أن تكون رقماً أكبر من صفر.' };
  }

  const notes = form.notes.trim();
  const input: RecordPackageServiceDeliveryInput = {
    assignment_id: assignmentId,
    service_id: form.serviceId,
    quantity: qty,
    ...(form.deliveredAt ? { delivered_at: form.deliveredAt } : {}),
    ...(notes ? { notes } : {}),
  };
  return { ok: true, input };
}

/** Today's date as 'YYYY-MM-DD' (local) for the date-input default. */
export function todayDateInputValue(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = (now.getMonth() + 1).toString().padStart(2, '0');
  const d = now.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
}
